// utils/rbac.ts - Role-Based Access Control System

export interface Role {
  id: number;
  name: string;
  description: string;
  permissions: string[];
}

export interface User {
  id: string;
  email: string;
  name: string;
  roleId: number;
  roleName: string;
  permissions?: string[]; // Add permissions from backend
  /** Assigned store/warehouse for staff; optional for company-wide roles */
  locationId?: string | number | null;
  locationName?: string | null;
  companyId?: string | null;
  companyPlanType?: string | null;
  profileImagePath?: string | null;
}

// Define all available permissions
export const PERMISSIONS = {
  // Products
  PRODUCT_CREATE: 'product_create',
  PRODUCT_READ: 'product_read',
  PRODUCT_UPDATE: 'product_update',
  PRODUCT_DELETE: 'product_delete',

  // Categories
  CATEGORY_CREATE: 'category_create',
  CATEGORY_READ: 'category_read',
  CATEGORY_UPDATE: 'category_update',
  CATEGORY_DELETE: 'category_delete',

  // Vendors
  VENDOR_CREATE: 'vendor_create',
  VENDOR_READ: 'vendor_read',
  VENDOR_UPDATE: 'vendor_update',
  VENDOR_DELETE: 'vendor_delete',
  VENDOR_PRODUCT_MANAGE: 'vendor_product_manage',

  // Sales
  SALES_CREATE: 'sales_create',
  SALES_READ: 'sales_read',
  SALES_UPDATE: 'sales_update',
  SALES_DELETE: 'sales_delete',

  /** Point of sale: cart, checkout, stashed sales, POS refunds (separate from Sales list page) */
  POS_MANAGE: 'pos_manage',

  // Purchase Orders (backend uses 'purchase_' prefix)
  PO_CREATE: 'purchase_create',
  PO_READ: 'purchase_read',
  PO_UPDATE: 'purchase_update',
  PO_DELETE: 'purchase_delete',
  PO_SEND: 'purchase_send',

  // Goods Receive
  GOODS_RECEIVE: 'goods_receive',
  GOODS_RECEIVE_COMPLETE: 'goods_receive_complete',

  // Stock Management
  STOCK_MOVEMENT_CREATE: 'stock_movement_create',
  STOCK_ADJUSTMENT_CREATE: 'stock_adjustment_create',
  STOCK_ADJUSTMENT_APPROVE: 'stock_adjustment_approve',
  STOCK_TRANSFER_CREATE: 'stock_transfer_create',
  STOCK_TRANSFER_RECEIVE: 'stock_transfer_receive',

  // Inventory
  INVENTORY_CREATE: 'inventory_create',
  INVENTORY_READ: 'inventory_read',
  INVENTORY_UPDATE: 'inventory_update',
  INVENTORY_DELETE: 'inventory_delete',

  // Users
  USER_CREATE: 'user_create',
  USER_READ: 'user_read',
  USER_UPDATE: 'user_update',
  USER_DELETE: 'user_delete',

  // Roles
  ROLE_CREATE: 'role_create',
  ROLE_READ: 'role_read',
  ROLE_UPDATE: 'role_update',
  ROLE_DELETE: 'role_delete',

  // Locations
  LOCATION_CREATE: 'location_create',
  LOCATION_READ: 'location_read',
  LOCATION_UPDATE: 'location_update',
  LOCATION_DELETE: 'location_delete',
  /** PO / GRPO / POS / sales data for all company locations (backend-enforced) */
  LOCATIONS_VIEW_ALL: 'locations_view_all',

  // Documents
  DOCUMENT_UPLOAD: 'document_upload',
  DOCUMENT_READ: 'document_read',
  DOCUMENT_UPDATE: 'document_update',
  DOCUMENT_DELETE: 'document_delete',

  // Reports
  REPORT_VIEW: 'report_view',
  REPORT_EXPORT: 'report_export',

  // Dashboard
  DASHBOARD_VIEW: 'dashboard_view',

  /** Company admin portal / POS payment admin APIs */
  ADMIN_ACCESS: 'admin_access',

  // Generic permissions
  EDIT_CONTENT: 'edit_content',
  MANAGE_USERS: 'manage_users',
  MANAGE_SETTINGS: 'manage_settings',
  VIEW_ANALYTICS: 'view_analytics',
} as const;

/** Permissions that grant access to inventory pages and stock/inventory APIs. */
export const INVENTORY_ACCESS_PERMISSIONS: string[] = [
  PERMISSIONS.INVENTORY_CREATE,
  PERMISSIONS.INVENTORY_READ,
  PERMISSIONS.INVENTORY_UPDATE,
  PERMISSIONS.INVENTORY_DELETE,
  PERMISSIONS.STOCK_ADJUSTMENT_CREATE,
  PERMISSIONS.STOCK_ADJUSTMENT_APPROVE,
  PERMISSIONS.STOCK_MOVEMENT_CREATE,
  PERMISSIONS.STOCK_TRANSFER_CREATE,
  PERMISSIONS.STOCK_TRANSFER_RECEIVE,
  PERMISSIONS.GOODS_RECEIVE,
  PERMISSIONS.GOODS_RECEIVE_COMPLETE,
];

/** Match route requiredRole against the user's role (supports admin-* custom names). */
export function matchesRequiredRole(
  requiredRole: string,
  roleName?: string | null,
  roleId?: number | string | null,
): boolean {
  const required = (requiredRole || '').trim().toLowerCase();
  const actual = (roleName || '').trim().toLowerCase();
  if (!required || !actual) return false;
  if (required === 'super admin') return actual === 'super admin';
  if (required === 'admin') {
    if (actual === 'super admin') return false;
    return actual === 'admin' || actual.startsWith('admin-') || Number(roleId) === ROLES.ADMIN.id;
  }
  return actual === required;
}

// Define role objects first (avoid referencing a key that doesn't exist)
// Admin: Sales/PO reports + User/Location management + read-only for dropdowns (roles, vendors, products)
const ADMIN_ROLE: Role = {
  id: 1,
  name: 'Admin',
  description: 'Admin with Sales Report, PO Report, User and Location management',
  permissions: [
    PERMISSIONS.SALES_READ,
    PERMISSIONS.POS_MANAGE,
    PERMISSIONS.PO_READ,
    PERMISSIONS.REPORT_VIEW,
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.USER_CREATE,
    PERMISSIONS.USER_READ,
    PERMISSIONS.USER_UPDATE,
    PERMISSIONS.USER_DELETE,
    PERMISSIONS.LOCATION_CREATE,
    PERMISSIONS.LOCATION_READ,
    PERMISSIONS.LOCATION_UPDATE,
    PERMISSIONS.LOCATION_DELETE,
    PERMISSIONS.LOCATIONS_VIEW_ALL,
    PERMISSIONS.ROLE_READ,
    PERMISSIONS.VENDOR_READ,
    PERMISSIONS.PRODUCT_READ,
    PERMISSIONS.PRODUCT_CREATE,
    PERMISSIONS.PRODUCT_UPDATE,
    PERMISSIONS.PRODUCT_DELETE,
    PERMISSIONS.CATEGORY_CREATE,
    PERMISSIONS.CATEGORY_READ,
    PERMISSIONS.CATEGORY_UPDATE,
    PERMISSIONS.CATEGORY_DELETE,
  ],
};

const GENERAL_MANAGER_ROLE: Role = {
  id: 2,
  name: 'General Manager',
  description: 'General Manager with multi-location access',
  permissions: [
    PERMISSIONS.PRODUCT_CREATE, PERMISSIONS.PRODUCT_READ, PERMISSIONS.PRODUCT_UPDATE, PERMISSIONS.PRODUCT_DELETE,
    PERMISSIONS.CATEGORY_CREATE,
    PERMISSIONS.CATEGORY_READ,
    PERMISSIONS.CATEGORY_UPDATE,
    PERMISSIONS.CATEGORY_DELETE,
    PERMISSIONS.VENDOR_CREATE, PERMISSIONS.VENDOR_READ, PERMISSIONS.VENDOR_UPDATE, PERMISSIONS.VENDOR_DELETE,
    PERMISSIONS.VENDOR_PRODUCT_MANAGE,
    PERMISSIONS.PO_CREATE, PERMISSIONS.PO_READ, PERMISSIONS.PO_UPDATE, PERMISSIONS.PO_SEND,
    PERMISSIONS.GOODS_RECEIVE, PERMISSIONS.GOODS_RECEIVE_COMPLETE,
    PERMISSIONS.STOCK_MOVEMENT_CREATE, PERMISSIONS.STOCK_ADJUSTMENT_CREATE, PERMISSIONS.STOCK_ADJUSTMENT_APPROVE,
    PERMISSIONS.STOCK_TRANSFER_CREATE, PERMISSIONS.STOCK_TRANSFER_RECEIVE,
    PERMISSIONS.SALES_CREATE, PERMISSIONS.SALES_READ,
    PERMISSIONS.POS_MANAGE,
    PERMISSIONS.DOCUMENT_UPLOAD, PERMISSIONS.DOCUMENT_READ, PERMISSIONS.DOCUMENT_UPDATE, PERMISSIONS.DOCUMENT_DELETE,
    PERMISSIONS.LOCATIONS_VIEW_ALL,
  ],
};

// Manager: full operational access, but NO Users/Locations/Roles CRUD permissions.
// (Those actions are Admin-only on backend; this keeps UI consistent.)
const MANAGER_ROLE: Role = {
  id: 8,
  name: 'Manager',
  description: 'Manager with full operational access (no Users/Locations/Roles CRUD)',
  permissions: (Object.values(PERMISSIONS) as string[]).filter((p) => {
    return !(
      p.startsWith('user_') ||
      p.startsWith('location_') ||
      p.startsWith('role_')
    );
  }),
};

const STORE_MANAGER_ROLE: Role = {
  id: 3,
  name: 'Store Manager',
  description: 'Store Manager with location-specific management access',
  permissions: [
    PERMISSIONS.PRODUCT_READ, PERMISSIONS.CATEGORY_READ, PERMISSIONS.VENDOR_READ,
    PERMISSIONS.PO_CREATE, PERMISSIONS.PO_READ, PERMISSIONS.PO_UPDATE, PERMISSIONS.PO_SEND,
    PERMISSIONS.GOODS_RECEIVE, PERMISSIONS.GOODS_RECEIVE_COMPLETE,
    PERMISSIONS.STOCK_MOVEMENT_CREATE, PERMISSIONS.STOCK_ADJUSTMENT_CREATE,
    PERMISSIONS.STOCK_TRANSFER_CREATE, PERMISSIONS.STOCK_TRANSFER_RECEIVE,
    PERMISSIONS.SALES_CREATE, PERMISSIONS.SALES_READ,
    PERMISSIONS.POS_MANAGE,
    PERMISSIONS.DOCUMENT_UPLOAD, PERMISSIONS.DOCUMENT_READ, PERMISSIONS.DOCUMENT_UPDATE
  ],
};

const SALES_EXECUTIVE_ROLE: Role = {
  id: 4,
  name: 'Sales Executive',
  description: 'Sales staff with sales and basic inventory access',
  permissions: [
    PERMISSIONS.PRODUCT_READ, PERMISSIONS.CATEGORY_READ, PERMISSIONS.VENDOR_READ,
    PERMISSIONS.SALES_CREATE, PERMISSIONS.SALES_READ,
    PERMISSIONS.POS_MANAGE,
    PERMISSIONS.STOCK_MOVEMENT_CREATE,
    PERMISSIONS.DOCUMENT_UPLOAD, PERMISSIONS.DOCUMENT_READ
  ],
};

const CASHIER_ROLE: Role = {
  id: 5,
  name: 'Cashier',
  description: 'Cashier with sales processing and basic inventory access',
  permissions: [
    PERMISSIONS.PRODUCT_READ,
    PERMISSIONS.CATEGORY_READ,
    PERMISSIONS.SALES_CREATE,
    PERMISSIONS.SALES_READ,
    PERMISSIONS.POS_MANAGE,
    PERMISSIONS.DOCUMENT_UPLOAD,
    PERMISSIONS.DOCUMENT_READ,
    PERMISSIONS.DASHBOARD_VIEW
  ],
};

const WAREHOUSE_STAFF_ROLE: Role = {
  id: 6,
  name: 'Warehouse Staff',
  description: 'Warehouse staff with inventory management access',
  permissions: [
    PERMISSIONS.PRODUCT_READ, PERMISSIONS.CATEGORY_READ, PERMISSIONS.VENDOR_READ,
    PERMISSIONS.GOODS_RECEIVE, PERMISSIONS.GOODS_RECEIVE_COMPLETE,
    PERMISSIONS.STOCK_MOVEMENT_CREATE, PERMISSIONS.STOCK_ADJUSTMENT_CREATE,
    PERMISSIONS.STOCK_TRANSFER_CREATE, PERMISSIONS.STOCK_TRANSFER_RECEIVE,
    PERMISSIONS.DOCUMENT_UPLOAD, PERMISSIONS.DOCUMENT_READ
  ],
};

// Add a basic VIEWER role for minimal access (if referenced)
const VIEWER_ROLE: Role = {
  id: 7,
  name: 'Viewer',
  description: 'Read-only viewer with minimal access',
  permissions: [
    PERMISSIONS.PRODUCT_READ,
    PERMISSIONS.CATEGORY_READ,
    PERMISSIONS.VENDOR_READ,
    PERMISSIONS.SALES_READ,
    PERMISSIONS.DOCUMENT_READ,
    PERMISSIONS.DASHBOARD_VIEW
  ],
};

const INVENTORY_ROLE: Role = {
  id: 9,
  name: 'Inventory',
  description: 'Inventory staff with edit, view and adjust inventory products access',
  permissions: [
    PERMISSIONS.PRODUCT_READ,
    PERMISSIONS.INVENTORY_READ,
    PERMISSIONS.INVENTORY_CREATE,
    PERMISSIONS.INVENTORY_UPDATE,
    PERMISSIONS.STOCK_ADJUSTMENT_CREATE,
    PERMISSIONS.STOCK_MOVEMENT_CREATE,
    PERMISSIONS.STOCK_TRANSFER_CREATE,
    PERMISSIONS.STOCK_TRANSFER_RECEIVE,
    PERMISSIONS.LOCATION_READ,
    PERMISSIONS.CATEGORY_READ,
    PERMISSIONS.DOCUMENT_READ,
    PERMISSIONS.DASHBOARD_VIEW,
  ],
};

// Create aliases for names referenced in the app that were missing
const INVENTORY_MANAGER_ROLE = INVENTORY_ROLE;

// Now export a single ROLES map using the defined objects
export const ROLES: Record<string, Role> = {
  ADMIN: ADMIN_ROLE,
  GENERAL_MANAGER: GENERAL_MANAGER_ROLE,
  MANAGER: MANAGER_ROLE,
  STORE_MANAGER: STORE_MANAGER_ROLE,
  SALES_EXECUTIVE: SALES_EXECUTIVE_ROLE,
  CASHIER: CASHIER_ROLE,
  WAREHOUSE_STAFF: WAREHOUSE_STAFF_ROLE,
  INVENTORY: INVENTORY_ROLE,
  INVENTORY_MANAGER: INVENTORY_MANAGER_ROLE,
  VIEWER: VIEWER_ROLE
};

/** Company admin and super admin keep the short inactivity logout. Shop users stay signed in. */
export function isAdminOrSuperAdminRole(roleName?: string | null): boolean {
  const role = (roleName || '').trim().toLowerCase();
  if (!role) return false;
  if (role.includes('super admin') || role.includes('superadmin')) return true;
  if (role === 'admin' || role.startsWith('admin-') || role.startsWith('admin ')) return true;
  return false;
}

// RBAC Helper Functions
export class RBACService {
  private static currentUser: User | null = null;
  private static sessionTimeout: number | null = null;
  private static warningTimeout: number | null = null;
  private static readonly SESSION_DURATION = 5 * 60 * 1000; // 5 minutes inactivity timeout
  private static readonly WARNING_TIME = 60 * 1000; // warn 1 minute before expiry

  // Set current user
  static setCurrentUser(user: User): void {
    this.currentUser = user;
    const sessionData = {
      user,
      loginTime: Date.now(),
      expiresAt: Date.now() + this.SESSION_DURATION
    };
    localStorage.setItem('currentUser', JSON.stringify(sessionData));

    // Clear any existing timeout
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
    }

    // Set session timeout
    this.setSessionTimeout();
  }

  // Set session timeout
  private static setSessionTimeout(): void {
    const sessionData = JSON.parse(localStorage.getItem('currentUser') || '{}');
    if (!isAdminOrSuperAdminRole(sessionData.user?.roleName)) {
      if (this.sessionTimeout) {
        clearTimeout(this.sessionTimeout);
        this.sessionTimeout = null;
      }
      if (this.warningTimeout) {
        clearTimeout(this.warningTimeout);
        this.warningTimeout = null;
      }
      return;
    }
    if (!sessionData.expiresAt) return;

    // Always clear previous timers before setting new ones.
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
      this.sessionTimeout = null;
    }
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      this.warningTimeout = null;
    }

    const timeUntilExpiry = sessionData.expiresAt - Date.now();
    const timeUntilWarning = timeUntilExpiry - this.WARNING_TIME;

    // Set warning timeout
    if (timeUntilWarning > 0) {
      this.warningTimeout = window.setTimeout(() => {
        this.showSessionWarning();
      }, timeUntilWarning);
    }

    // Set expiry timeout
    if (timeUntilExpiry > 0) {
      this.sessionTimeout = window.setTimeout(() => {
        this.handleSessionExpiry();
      }, timeUntilExpiry);
    } else {
      // Session already expired
      this.handleSessionExpiry();
    }
  }

  // Show session warning
  private static showSessionWarning(): void {
    // This will be handled by a component that listens for session events
    const event = new CustomEvent('sessionWarning', {
      detail: { minutesLeft: 5 }
    });
    window.dispatchEvent(event);
  }

  // Handle session expiry
  private static handleSessionExpiry(): void {
    this.clearUser();
    const event = new CustomEvent('sessionExpired');
    window.dispatchEvent(event);
  }

  // Extend session (called on user activity)
  static extendSession(): void {
    const sessionData = JSON.parse(localStorage.getItem('currentUser') || '{}');
    if (sessionData.user) {
      sessionData.expiresAt = Date.now() + this.SESSION_DURATION;
      localStorage.setItem('currentUser', JSON.stringify(sessionData));

      // Timers are cleared/replaced inside setSessionTimeout.
      this.setSessionTimeout();
    }
  }

  // Get current user
  static getCurrentUser(): User | null {
    const stored = localStorage.getItem('currentUser');
    if (!stored) return null;

    const sessionData = JSON.parse(stored);

    // Shop users stay signed in. Admin and super admin still expire on inactivity.
    const privileged = isAdminOrSuperAdminRole(sessionData.user?.roleName);
    if (privileged && sessionData.expiresAt && Date.now() > sessionData.expiresAt) {
      this.clearUser();
      return null;
    }

    if (sessionData.user) {
      this.currentUser = sessionData.user;
      // Ensure timeout is set
      if (!this.sessionTimeout) {
        this.setSessionTimeout();
      }
      return this.currentUser;
    }

    return null;
  }

  // Get user's role
  static getUserRole(): Role | null {
    const user = this.getCurrentUser();
    if (!user) return null;

    const roleId = Number(user.roleId);
    return Object.values(ROLES).find((role) => role.id === roleId) || null;
  }

  // Check if user has specific permission
  static hasPermission(permission: string): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;

    const roleNameLower = user.roleName?.toLowerCase() || '';
    const roleId = Number(user.roleId);

    // Super Admin has ALL permissions — always return true
    if (roleNameLower === 'super admin') {
      return true;
    }

    // Admin has view-only for purchase orders: never grant create/update/delete/send
    const isAdmin =
      roleNameLower === 'admin' ||
      roleNameLower.startsWith('admin-') ||
      roleId === ROLES.ADMIN.id;
    const poWritePermissions = [PERMISSIONS.PO_CREATE, PERMISSIONS.PO_UPDATE, PERMISSIONS.PO_DELETE, PERMISSIONS.PO_SEND];
    if (isAdmin && (poWritePermissions as string[]).includes(permission)) {
      return false;
    }

    // Admin cannot create roles (Super Admin only)
    if (isAdmin && permission === PERMISSIONS.ROLE_CREATE) {
      return false;
    }

    // If user has permissions from backend, use those
    if (user.permissions && user.permissions.length > 0) {
      if (user.permissions.includes('all') || user.permissions.includes('*')) {
        return true;
      }
      return user.permissions.includes(permission);
    }

    // Fallback to role-based permissions
    const role = this.getUserRole();
    if (!role) return false;

    if (role.permissions.includes('all') || role.permissions.includes('*')) {
      return true;
    }
    return role.permissions.includes(permission);
  }

  // Check if user has any of the specified permissions
  static hasAnyPermission(permissions: string[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  // Check if user has all specified permissions
  static hasAllPermissions(permissions: string[]): boolean {
    return permissions.every(permission => this.hasPermission(permission));
  }

  /** Add/update vendor catalog lines (matches backend require_vendor_catalog_mutate). */
  static canWriteVendorCatalog(): boolean {
    return (
      this.hasPermission(PERMISSIONS.VENDOR_PRODUCT_MANAGE) ||
      (this.hasPermission(PERMISSIONS.VENDOR_CREATE) &&
        this.hasPermission(PERMISSIONS.VENDOR_UPDATE))
    );
  }

  /**
   * Remove a vendor catalog line (matches backend require_vendor_catalog_remove):
   * write access above, or vendor_delete.
   */
  static canDeleteVendorCatalogLine(): boolean {
    return (
      this.canWriteVendorCatalog() || this.hasPermission(PERMISSIONS.VENDOR_DELETE)
    );
  }

  /** @deprecated Prefer canWriteVendorCatalog / canDeleteVendorCatalogLine */
  static canManageVendorCatalog(): boolean {
    return this.canWriteVendorCatalog();
  }

  // Check if user is admin or super admin
  static isAdmin(): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;
    const r = user.roleName?.toLowerCase() || '';
    return r === 'admin' || r.startsWith('admin-') || r === 'super admin' || Number(user.roleId) === ROLES.ADMIN.id;
  }

  /** Tenant company Admin only — not Super Admin (platform root). */
  static isCompanyAdmin(): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;
    const r = user.roleName?.toLowerCase() || '';
    if (r === 'super admin') return false;
    return r === 'admin' || r.startsWith('admin-') || Number(user.roleId) === ROLES.ADMIN.id;
  }

  static isSuperAdmin(): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;
    return (user.roleName || '').toLowerCase().trim() === 'super admin';
  }

  // Clear user data (logout)
  static clearUser(): void {
    this.currentUser = null;
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
      this.sessionTimeout = null;
    }
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      this.warningTimeout = null;
    }
    localStorage.removeItem('currentUser');
    localStorage.removeItem('isAuthenticated');
    void import('../services/bootstrapService').then(({ invalidateBootstrapLookups }) => {
      invalidateBootstrapLookups();
    });
  }

  // Get accessible routes based on permissions
  static getAccessibleRoutes(): string[] {
    const role = this.getUserRole();
    if (!role) return [];

    const routes: string[] = [];

    // Dashboard - all authenticated users
    if (this.hasPermission(PERMISSIONS.DASHBOARD_VIEW)) {
      routes.push('/dashboard');
    }

    // Sales reports are sourced from POS transactions.
    if (this.hasPermission(PERMISSIONS.POS_MANAGE)) {
      routes.push('/sales');
    }

    if (this.hasPermission(PERMISSIONS.POS_MANAGE)) {
      routes.push('/pos');
    }

    // Purchase Orders
    if (this.hasAnyPermission([
      PERMISSIONS.PO_CREATE,
      PERMISSIONS.PO_READ,
      PERMISSIONS.PO_UPDATE,
      PERMISSIONS.PO_DELETE
    ])) {
      routes.push('/purchase-orders');
    }

    // Inventory (inventory_* or operational stock permissions)
    if (this.hasAnyPermission(INVENTORY_ACCESS_PERMISSIONS)) {
      routes.push('/inventory');
    }

    // Products
    if (this.hasAnyPermission([
      PERMISSIONS.PRODUCT_CREATE,
      PERMISSIONS.PRODUCT_READ,
      PERMISSIONS.PRODUCT_UPDATE,
      PERMISSIONS.PRODUCT_DELETE
    ])) {
      routes.push('/products');
    }

    // Users / Locations: manage only in Admin Portal (not main app routes).

    // Documents
    if (this.hasAnyPermission([
      PERMISSIONS.DOCUMENT_UPLOAD,
      PERMISSIONS.DOCUMENT_READ,
      PERMISSIONS.DOCUMENT_DELETE
    ])) {
      routes.push('/documents');
    }

    // Reports
    if (this.hasAnyPermission([
      PERMISSIONS.REPORT_VIEW,
      PERMISSIONS.REPORT_EXPORT
    ])) {
      routes.push('/reports');
    }

    return routes;
  }
}

// Export getCurrentUser as a standalone function for backward compatibility
export const getCurrentUser = (): User | null => {
  return RBACService.getCurrentUser();
};

// Export other functions for backward compatibility
export const hasAnyPermission = (permissions: string[]): boolean => {
  return RBACService.hasAnyPermission(permissions);
};

export const logout = (): void => {
  RBACService.clearUser();
};

// Mock user database for demonstration
export const MOCK_USERS: Record<string, User> = {
  'admin@example.com': {
    id: '1',
    email: 'admin@example.com',
    name: 'Admin User',
    roleId: ROLES.ADMIN.id,
    roleName: ROLES.ADMIN.name,
  },
  'manager@example.com': {
    id: '2',
    email: 'manager@example.com',
    name: 'Manager User',
    roleId: ROLES.MANAGER.id,
    roleName: ROLES.MANAGER.name,
  },
  'inventory@example.com': {
    id: '3',
    email: 'inventory@example.com',
    name: 'Inventory Manager',
    roleId: ROLES.INVENTORY_MANAGER.id,
    roleName: ROLES.INVENTORY_MANAGER.name,
  },
  'cashier@example.com': {
    id: '4',
    email: 'cashier@example.com',
    name: 'Cashier User',
    roleId: ROLES.CASHIER.id,
    roleName: ROLES.CASHIER.name,
  },
  'viewer@example.com': {
    id: '5',
    email: 'viewer@example.com',
    name: 'Viewer User',
    roleId: ROLES.VIEWER.id,
    roleName: ROLES.VIEWER.name,
  },
};

// Helper to get user by email (for login simulation)
export const getUserByEmail = (email: string): User | null => {
  return MOCK_USERS[email.toLowerCase()] || null;
};